import { useNavigation } from '@/app/hooks'
import { Icon, Text } from '@/components'
import { IconNames } from '@/components/Icon/Icon'
import classes from '@/utils/classes/classes'

type Props = {
  iconName: IconNames
  title: string
  description?: React.ReactNode
  destinationPath: string
  children?: React.ReactNode
  className?: string
}

/**
 * A configure-devices setting row, per Figma:
 *
 *   [icon] Title
 *   description text...
 *   [ value content                    > ]   <- clickable grey rounded box
 *
 * `children` render inside the grey clickable box and act as the
 * tappable affordance (alongside the trailing chevron).
 */
export default function ConfigureSettingRow({
  iconName,
  title,
  description,
  destinationPath,
  children,
  className,
}: Props) {
  const navigation = useNavigation()

  return (
    <div className={classes('flex flex-col gap-2', className)}>
      <div className="flex flex-row items-center gap-2">
        <div className="flex-none flex items-center justify-center">
          <Icon name={iconName} size={20} color="base-purple-500" />
        </div>
        <Text variant="body1" className="font-bold text-themed-grey-900">
          {title}
        </Text>
      </div>
      {description && (
        <ConfigureSettingDescription>{description}</ConfigureSettingDescription>
      )}
      <div
        className="flex flex-row items-center gap-2 rounded-lg bg-themed-grey-100 p-2 cursor-pointer hover:bg-themed-grey-200 transition-colors"
        onClick={() => navigation.push(destinationPath)}
      >
        <div className="flex-1 min-w-0">
          <Text
            variant="body3"
            className="font-medium text-themed-grey-500 !text-[12px]"
          >
            {children}
          </Text>
        </div>
        <div className="flex-none flex items-center">
          <Icon name="ChevronRight" color="grey-400" size={16} />
        </div>
      </div>
    </div>
  )
}

export function ConfigureSettingDescription({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <Text
      variant="body4"
      className={classes('text-themed-grey-500 font-medium', className)}
    >
      {children}
    </Text>
  )
}
